<template>
  <section class="container-fluid py-3">
    <form class="row g-2 align-items-end mb-3" @submit.prevent="apply">
      <div class="col-12 col-sm-6 col-md-3 col-lg-2">
        <label class="form-label">Start</label>
        <input type="date" v-model="startDateStr" class="form-control" />
      </div>
      <div class="col-12 col-sm-6 col-md-3 col-lg-2">
        <label class="form-label">End</label>
        <input type="date" v-model="endDateStr" class="form-control" />
      </div>

      <div class="col-12 col-sm-6 col-md-3 col-lg-2">
        <label class="form-label">Level</label>
        <select v-model="level" class="form-select">
          <option value="">All</option>
          <option v-for="lvl in levels" :key="lvl" :value="lvl">{{ lvl }}</option>
        </select>
      </div>

      <div class="col-12 col-sm-6 col-md-3 col-lg-2">
        <label class="form-label">Application</label>
        <select v-model="application" class="form-select">
          <option value="">All</option>
          <option v-for="app in applications" :key="app" :value="app">{{ app }}</option>
        </select>
      </div>

      <div class="col-12 col-sm-6 col-md-3 col-lg-2">
        <label class="form-label">Operation</label>
        <select v-model="operationType" class="form-select">
          <option value="">All</option>
          <option v-for="op in operationTypes" :key="op" :value="op">{{ op }}</option>
        </select>
      </div>

      <div class="col-12 col-sm-6 col-md-3 col-lg-2">
        <label class="form-label">Server</label>
        <select v-model="serverName" class="form-select">
          <option value="">All</option>
          <option v-for="s in servers" :key="s" :value="s">{{ s }}</option>
        </select>
      </div>

      <div class="col-12 col-sm-6 col-md-3 col-lg-2">
        <label class="form-label">Results</label>
        <select v-model.number="maxResults" class="form-select" @change="apply">
          <option :value="50">50</option>
          <option :value="100">100</option>
          <option :value="500">500</option>
        </select>
      </div>

      <div class="col-auto d-flex gap-2">
        <button type="submit" class="btn btn-primary" :disabled="loading">Apply</button>
        <button type="button" class="btn btn-secondary" :disabled="loading" @click="reset">Reset</button>
      </div>
    </form>

    <div v-if="loading" class="d-flex align-items-center mb-3">
      <div class="spinner-border me-2" role="status"><span class="visually-hidden">Loading...</span></div>
      <span>Loading…</span>
    </div>

    <div v-else-if="error" class="alert alert-danger" role="alert">
      {{ error }}
    </div>

    <div v-else>
      <div class="small text-muted mb-2">
        Showing {{ rows.length }} of page size {{ maxResults }}
      </div>

      <div class="table-responsive">
        <table v-if="rows.length" class="table table-sm table-hover align-middle">
          <thead class="table-light">
            <tr>
              <th scope="col">Local Time</th>
              <th scope="col">Level</th>
              <th scope="col">App</th>
              <th scope="col">Op</th>
              <th scope="col">Server</th>
              <th scope="col">Message</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(r, i) in rows" :key="i">
              <tr @click="toggle(i)" style="cursor: pointer;">
                <td>{{ toLocal(r.Timestamp as any) }}</td>
                <td><span :class="levelBadgeClass((r as any).Level)">{{ (r as any).Level }}</span></td>
                <td>{{ (r as any).Application }}</td>
                <td>{{ (r as any).OperationType }}</td>
                <td>{{ (r as any).ServerName }}</td>
                <td class="text-truncate" style="max-width: 420px;">{{ truncate((r as any).Message) }}</td>
              </tr>
              <tr v-show="expandedIndex === i" class="table-active">
                <td colspan="6">
                  <div class="mb-2"><strong>Message</strong></div>
                  <pre class="mb-2">{{ (r as any).Message }}</pre>
                  <div v-if="(r as any).Exception" class="mb-2"><strong>Exception</strong></div>
                  <pre v-if="(r as any).Exception" class="mb-2">{{ (r as any).Exception }}</pre>
                  <div class="text-muted small mb-2">
                    PID: {{ (r as any).ProcessId }} • {{ (r as any).ProcessName }} • TID: {{ (r as any).ThreadId }} •
                    IP: {{ (r as any).ClientIp }} •
                    Corr: {{ (r as any).CorrelationId }} • Ref: {{ (r as any).Referer }}
                  </div>

                  <div v-if="(r as any).Properties" class="mt-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                      <strong class="mb-0">Properties</strong>
                      <button type="button" class="btn btn-sm btn-outline-secondary"
                        @click.stop="copyJson((r as any).Properties)">Copy JSON</button>
                    </div>
                    <div class="table-responsive">
                      <table class="table table-sm align-middle">
                        <tbody>
                          <tr v-for="(val, key) in sortedProps((r as any).Properties)" :key="key">
                            <th style="width:220px;" class="text-nowrap">{{ key }}</th>
                            <td>
                              <template v-if="isJsonLike(val)">
                                <pre class="mb-0 small bg-light border rounded p-2 font-monospace text-break">{{ pretty(val) }}</pre>
                              </template>
                              <code v-else class="text-break">{{ val }}</code>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div v-if="!rows.length" class="alert alert-info" role="alert">
        No logs found for the selected filters.
      </div>

      <nav class="mt-2" aria-label="Logs paging" v-if="rows.length">
        <ul class="pagination pagination-sm mb-0">
          <li class="page-item" :class="{ disabled: !canPrev }">
            <button class="page-link" @click="prev" :disabled="!canPrev">Prev</button>
          </li>
          <li class="page-item" :class="{ disabled: !canNext }">
            <button class="page-link" @click="next" :disabled="!canNext">Next</button>
          </li>
        </ul>
      </nav>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { getFilters, getLogs } from '../api'
import type { FiltersResponse, LogRow } from '../types'
import { startOfDayLocal, endOfDayLocal, toUtcIso, defaultDateRange, fromUtcToLocalString } from '../date'

type AnyLogRow = LogRow & { Properties?: Record<string, any> }

const levels = ref<string[]>([])
const applications = ref<string[]>([])
const operationTypes = ref<string[]>([])
const servers = ref<string[]>([])

const level = ref<string>('')
const application = ref<string>('')
const operationType = ref<string>('')
const serverName = ref<string>('')
const maxResults = ref<number>(100)

const startDateStr = ref<string>('')
const endDateStr = ref<string>('')

const rows = ref<AnyLogRow[]>([])
const totalCount = ref<number>(0)
const loading = ref<boolean>(false)
const error = ref<string | null>(null)

const nextToken = ref<string | null>(null)
const history = ref<(string | null)[]>([null])
const canPrev = computed(() => history.value.length > 1)
const canNext = computed(() => !!nextToken.value)

const expandedIndex = ref<number | null>(null)
function toggle(i:number){ expandedIndex.value = expandedIndex.value===i ? null : i }

function truncate(s: string | null, n = 120) { if (!s) return ''; return s.length > n ? s.slice(0, n) + '…' : s }
function toLocal(utcIso: string | null) { return fromUtcToLocalString(utcIso as any) }

function levelBadgeClass(lvl: string | null) {
  const map: Record<string, string> = {
    'Fatal': 'badge text-bg-dark',
    'Error': 'badge text-bg-danger',
    'Warning': 'badge text-bg-warning',
    'Information': 'badge text-bg-info',
    'Debug': 'badge text-bg-secondary'
  }
  return map[lvl || ''] || 'badge text-bg-secondary'
}

function mergeMinimums(f: FiltersResponse) {
  const minLevels = ['Error','Warning','Fatal','Information','Debug']
  const minApps = ['IisServerManager','Components','LazarusApi']
  levels.value = Array.from(new Set([...(f.Levels ?? []), ...minLevels]))
  applications.value = Array.from(new Set([...(f.Applications ?? []), ...minApps]))
  operationTypes.value = f.OperationTypes ?? []
  servers.value = f.Servers ?? []
}

function setDefaultDates(){
  const { start, end } = defaultDateRange()
  startDateStr.value = start.toISOString().slice(0,10)
  endDateStr.value = end.toISOString().slice(0,10)
}

function buildQuery(token: string | null){
  const startLocal = startOfDayLocal(new Date(startDateStr.value))
  const endLocal   = endOfDayLocal(new Date(endDateStr.value))
  return {
    startDate: toUtcIso(startLocal),
    endDate: toUtcIso(endLocal),
    level: level.value || undefined,
    application: application.value || undefined,
    operationType: operationType.value || undefined,
    serverName: serverName.value || undefined,
    maxResults: maxResults.value,
    continuationToken: token ?? undefined,
  }
}

async function loadPage(token: string | null){
  try {
    loading.value = true
    error.value = null

    const resp: any = await getLogs(buildQuery(token))
    rows.value = (resp.Data ?? []) as AnyLogRow[]
    totalCount.value = resp.Count ?? rows.value.length
    nextToken.value = resp.NextToken ?? null
  } catch (e:any) {
    error.value = e?.message ?? 'Failed to load logs'
    rows.value = []
    totalCount.value = 0
    nextToken.value = null
  } finally {
    loading.value = false
  }
}

async function apply(){
  history.value = [null]
  expandedIndex.value = null
  await loadPage(null)
}

async function reset(){
  level.value = ''
  application.value = ''
  operationType.value = ''
  serverName.value = ''
  maxResults.value = 100
  setDefaultDates()
  await apply()
}

async function next(){
  if (!nextToken.value) return
  history.value.push(nextToken.value)
  expandedIndex.value = null
  await loadPage(nextToken.value)
}

async function prev(){
  if (history.value.length <= 1) return
  history.value.pop()
  const previousToken = history.value[history.value.length - 1]
  expandedIndex.value = null
  await loadPage(previousToken ?? null)
}

function pretty(v: any) {
  try {
    if (typeof v === 'string' && (v.startsWith('{') || v.startsWith('['))) return JSON.stringify(JSON.parse(v), null, 2)
    return JSON.stringify(v, null, 2)
  } catch { return String(v) }
}
function isJsonLike(v: any) {
if (v === null) return false
if (typeof v === 'object') return true
if (typeof v === 'string') {
const s = v.trim()
if (s.startsWith('{') || s.startsWith('[')) return true
}
return false
}
function sortedProps(props: Record<string, any> | null | undefined) {
  if (!props) return {}
  return Object.fromEntries(Object.keys(props).sort().map(k => [k, (props as any)[k]]))
}
async function copyJson(props: any) {
  try {
    const text = typeof props === 'string' ? props : JSON.stringify(props, null, 2)
    await navigator.clipboard.writeText(text)
  } catch { }
}

onMounted(async () => {
  try {
    loading.value = true
    const f = await getFilters()
    mergeMinimums(f)
  } catch (e:any) {
    error.value = e?.message ?? 'Failed to load filters'
  } finally {
    setDefaultDates()
    await apply()
    loading.value = false
  }
})
</script>
